import React, { useMemo, useState } from 'react';
import {
  useTable,
  SortingState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import ColumnSelector from '../components/common/ColumnSelector';
import { DataTable, DataTableToolbar } from '../components/common/DataTable';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { createUserColumns } from '../components/users/userColumns';
import {
  USER_COLUMN_METADATA,
  USER_DEFAULT_COLUMNS,
} from '../components/users/userColumnMetadata';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import {
  useListUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useChangePasswordMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
} from '@store/api/userApi';
import type { User, CreateUserRequest } from '../types/api';
import errorService from '@services/errorService';

const UserManagementPage: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [includeInactive] = useState(false);
  const { data: users = [], isLoading, error } = useListUsersQuery({ includeInactive });

  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [changePassword] = useChangePasswordMutation();
  const [deactivateUser] = useDeactivateUserMutation();
  const [reactivateUser] = useReactivateUserMutation();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [formData, setFormData] = useState<Partial<CreateUserRequest>>({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'viewer',
  });

  const [newPassword, setNewPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleCreateUser = async () => {
    setSubmitError(null);
    try {
      const payload = {
        ...formData,
        role: (formData.role?.toUpperCase() || 'VIEWER') as 'admin' | 'operator' | 'viewer',
      };
      await createUser(payload as CreateUserRequest).unwrap();
      setCreateDialogOpen(false);
      setFormData({
        username: '',
        password: '',
        email: '',
        full_name: '',
        role: 'viewer',
      });
    } catch (err) {
      setSubmitError(errorService.toUserMessage(err));
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setSubmitError(null);
    try {
      await updateUser({
        username: selectedUser.username,
        data: {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role?.toUpperCase() as 'admin' | 'operator' | 'viewer',
        },
      }).unwrap();
      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setSubmitError(errorService.toUserMessage(err));
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) return;
    setSubmitError(null);
    try {
      await changePassword({
        username: selectedUser.username,
        data: { new_password: newPassword },
      }).unwrap();
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (err) {
      setSubmitError(errorService.toUserMessage(err));
    }
  };

  const handleDeactivateUser = async (username: string) => {
    if (!confirm(`Are you sure you want to deactivate user "${username}"?`)) return;
    try {
      await deactivateUser(username).unwrap();
    } catch (err) {
      alert(errorService.toUserMessage(err));
    }
  };

  const handleReactivateUser = async (username: string) => {
    try {
      await reactivateUser(username).unwrap();
    } catch (err) {
      alert(errorService.toUserMessage(err));
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role.toLowerCase() as 'admin' | 'operator' | 'viewer',
    });
    setEditDialogOpen(true);
  };

  const openPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const {
    visibleColumns,
    toggleColumn,
    resetToDefault,
    applyPreset,
    columnVisibility,
  } = useColumnVisibility({
    columns: USER_COLUMN_METADATA,
    storageKey: 'userManagement.columnVisibility.v1',
    defaultColumns: USER_DEFAULT_COLUMNS,
  });


  const userColumns = useMemo(
    () =>
      createUserColumns({
        onEdit: openEditDialog,
        onChangePassword: openPasswordDialog,
        onDeactivate: handleDeactivateUser,
        onReactivate: handleReactivateUser,
      }),
    // Safe to build once: the handlers only touch state setters and RTK
    // mutation triggers, all of which keep their identity across renders.
    []
  );

  const usersTable = useTable({
    features,
    data: users,
    columns: userColumns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getRowId: (row) => row.username,
  });

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create User
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <DataTableToolbar
          actions={
            <ColumnSelector
              columns={USER_COLUMN_METADATA}
              visibleColumns={visibleColumns}
              onColumnToggle={toggleColumn}
              onReset={resetToDefault}
              onPresetSelect={applyPreset}
            />
          }
        />

        <DataTable
          table={usersTable}
          ariaLabel="Users"
          isLoading={isLoading}
          emptyMessage="No users found"
          minWidth={1100}
        />
      </Paper>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            required
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            label="Full Name"
            fullWidth
            margin="normal"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
              label="Role"
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="operator">Operator</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={!formData.username || !formData.password}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {selectedUser?.username}</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            label="Full Name"
            fullWidth
            margin="normal"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
              label="Role"
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="operator">Operator</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password: {selectedUser?.username}</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            label="New Password"
            type="password"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={!newPassword}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
